import { useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

import { DeepLinkType } from '@gem/models/deeplinksModel';

import Tasks from 'APP/Tasks';
import { useDeepLinkParams } from 'APP/main/Modules/DeepLinkHandler/DeepLinkHandler.hooks';
import { StickerPackPopup } from 'APP/main/PopupManager/Popups/StickerPackPopup/StickerPackPopup';
import { RouterParams, RouterState } from 'APP/router/constants';
import Entities from 'STORE';

interface IDeepLinkHandlerPresenter {
  finishDeepLinkHandling(): void;
}

export async function useDeepLinkHandlerPresenter({
  finishDeepLinkHandling,
}: IDeepLinkHandlerPresenter): Promise<void> {
  const navigate = useNavigate();
  const { getParams, clearGetParameters } = useDeepLinkParams();

  const handleDeepLink = useCallback(async (): Promise<void> => {
    const { isDeepLink, expired, entityId, parentId, type, channelId } = getParams;

    if (!isDeepLink) {
      finishDeepLinkHandling();
      return;
    }

    const params = await Tasks.deeplinks.getParamsByDeepLink({
      deepLink: {
        entityId,
        parentId,
        type,
        expired,
        channelId,
      },
    });

    if (!params) {
      return;
    }

    const {
      groupRoute,
      messageId,
      stickerPackId,
      groupId,
      spaceId,
      userId,
      isAlreadyJoined,
      spaceRoute,
      channelId: callChannelId,
    } = params;

    if (type === DeepLinkType.Room && callChannelId) {
      navigate(`?${RouterParams.roomCallChannelId}=${callChannelId}`, { replace: true });
    }

    if (userId) {
      const me = Entities.usersStore.Me;
      if (me?.id === userId) {
        await Tasks.group.goToSavedMessages();
      } else {
        await Tasks.group.createP2PGroup(userId);
      }

      finishDeepLinkHandling();
      return;
    }

    if (stickerPackId) {
      Tasks.app.openPopup(StickerPackPopup, { params: { stickerPackId } });

      finishDeepLinkHandling();
      return;
    }

    if (spaceRoute) {
      navigate(spaceRoute, { replace: true, state: { [RouterState.fromDeepLink]: true } });
      finishDeepLinkHandling();
      return;
    }

    if (!groupRoute) {
      finishDeepLinkHandling();
      return;
    }

    if (groupRoute && isAlreadyJoined && groupId) {
      Tasks.calls.findGroupCallAndRun(groupId);
    }

    if (groupRoute && !isAlreadyJoined && groupId) {
      const group = Entities.GroupsStore.getGroupById(groupId);
      group?.setIsAwaitCall(true);
    }

    await Tasks.group.setActiveGroup({ groupId, state: { [RouterState.fromDeepLink]: true } });
    navigate(groupRoute, { replace: true });

    if (groupId && messageId) {
      Tasks.messaging.focusMessage({ groupId, messageId });
    }

    if (spaceId) {
      await Tasks.space.setActiveSpace(spaceId);
    }

    finishDeepLinkHandling();
  }, []);

  useEffect(() => {
    handleDeepLink();
    if (getParams.isDeepLink) {
      clearGetParameters();
    }
  }, []);
}
